// hooks/useMultiFormValidation.ts
import { FieldValues, UseFormReturn } from "react-hook-form";
import { z } from "zod";

interface FormConfig<T extends FieldValues> {
  form: UseFormReturn<T>;
  schema: z.ZodType<T>;
  stepIndex: number;
}

interface UseMultiFormValidationResult {
  validateForms: () => Promise<{
    isValid: boolean;
    firstErrorStep: number | null;
  }>;
}

export function useMultiFormValidation(
  forms: FormConfig<any>[]
): UseMultiFormValidationResult {
  const validateForms = async () => {
    // Get all form data
    const formDataList = forms.map((config) => config.form.getValues());

    // Validate all forms in parallel
    const results = await Promise.all(
      forms.map((config, index) =>
        config.schema.safeParseAsync(formDataList[index])
      )
    );

    // Map errors back to forms
    results.forEach((result, index) => {
      if (!result.success) {
        result.error.errors.forEach((error) => {
          const fieldPath = error.path.join(".");
          forms[index].form.setError(fieldPath as any, {
            type: "manual",
            message: error.message,
          });
        });
      }
    });

    // Find first step with errors
    let firstErrorStep: number | null = null;
    for (let i = 0; i < results.length; i++) {
      if (!results[i].success) {
        firstErrorStep = forms[i].stepIndex;
        break;
      }
    }

    const isValid = results.every((result) => result.success);

    return {
      isValid,
      firstErrorStep,
    };
  };

  return { validateForms };
}
